import { useRef, useState } from 'react';
import type { MeinProjekt } from '@beholder/shared';
import {
  MAIL_GMAIL,
  type MailKonto,
  type MailKontoEingabe,
  type MailStatus,
  type MailUnsortiert,
  type MailVerbindungsTest,
} from '@beholder/shared/module/mail';
import type { GlobalProps } from '../../core/modul';
import { api } from '../../core/api';
import { Auswahl, Dialog, Eingabe, Feld, Karte, Knopf, Laden, Leer, Meldung, datumZeit, useAktion, useDaten } from '../../core/ui';
import { Absender, ArtMarke, LinkTabelle, Verdacht, wann } from './gemeinsam';

/** Projektübergreifende Seite /modul/mail – nur für Admins: Postfächer, jetzt abrufen, .eml einlesen, Posteingang „Unsortiert“. */
export function Postfach(_p: GlobalProps) {
  const status = useDaten(() => api.get<MailStatus>('/api/modul/mail/status'), []);
  const konten = useDaten(() => api.get<MailKonto[]>('/api/modul/mail/konten'), []);
  const [alle, setAlle] = useState(false);
  const [korb, setKorb] = useState(false);
  const liste = useDaten(
    () => api.get<MailUnsortiert[]>(`/api/modul/mail/unsortiert${korb ? '?papierkorb=1' : alle ? '?alle=1' : ''}`),
    [alle, korb],
  );
  const projekte = useDaten(() => api.get<MeinProjekt[]>('/api/projekte'), []);
  const abruf = useAktion();
  const einlesen = useAktion();
  const [meldung, setMeldung] = useState('');
  const [ziel, setZiel] = useState('');
  const [dialog, setDialog] = useState<{ konto: MailKonto | null } | null>(null);
  const datei = useRef<HTMLInputElement>(null);

  function allesNeu() {
    void status.neuLaden();
    void konten.neuLaden();
    void liste.neuLaden();
  }

  async function jetztAbrufen() {
    setMeldung('');
    const r = await abruf.ausfuehren(() => api.post<{ neu: number; laeuftSchon?: boolean; fehler: string[] }>('/api/modul/mail/abrufen'));
    if (r) {
      setMeldung(
        r.laeuftSchon ? 'Ein Abruf läuft gerade schon.' : r.neu ? `${r.neu} neue Mail${r.neu === 1 ? '' : 's'} geholt.` : r.fehler.length ? '' : 'Keine neuen Mails.',
      );
    }
    allesNeu();
  }

  async function emlEinlesen(dateien: FileList | null) {
    if (!dateien?.length) return;
    setMeldung('');
    const r = await einlesen.ausfuehren(() =>
      api.hochladen<{ name: string; doppelt: boolean; fehler: string | null }[]>(
        `/api/modul/mail/eml${ziel ? `?projektId=${ziel}` : ''}`,
        dateien,
      ),
    );
    if (datei.current) datei.current.value = '';
    if (r) {
      const neu = r.filter((x) => !x.doppelt && !x.fehler).length;
      const doppelt = r.filter((x) => x.doppelt).length;
      const kaputt = r.filter((x) => x.fehler);
      setMeldung(
        [
          `${neu} Mail${neu === 1 ? '' : 's'} eingelesen.`,
          doppelt ? `${doppelt} war${doppelt === 1 ? '' : 'en'} schon da.` : '',
          ...kaputt.map((k) => `${k.name}: ${k.fehler}`),
        ]
          .filter(Boolean)
          .join(' '),
      );
    }
    allesNeu();
  }

  const s = status.daten;
  const offeneProjekte = (projekte.daten ?? []).filter((p) => !p.archiviert);
  return (
    <div className="seite">
      <div className="seiten-kopf">
        <h1>Posteingang</h1>
      </div>
      <Karte
        titel="Postfächer"
        aktion={
          <div className="reihe">
            <Knopf disabled={!s?.eingerichtet || abruf.laeuft} onClick={jetztAbrufen}>
              {abruf.laeuft || s?.laeuft ? 'Wird abgerufen …' : 'Jetzt abrufen'}
            </Knopf>
            <Knopf art="haupt" onClick={() => setDialog({ konto: null })}>
              Konto hinzufügen
            </Knopf>
          </div>
        }
      >
        <div className="stapel">
          <Meldung>{konten.fehler || abruf.fehler}</Meldung>
          <Meldung art="ok">{meldung}</Meldung>
          {!konten.daten && !konten.fehler && <Laden />}
          {konten.daten?.length === 0 && (
            <Meldung art="info">
              Noch kein Postfach eingerichtet. Mit „Konto hinzufügen“ ein Gmail-Postfach (oder ein anderes mit IMAP) verbinden – es lassen sich
              mehrere anlegen. Bis dahin lassen sich Mails als .eml-Datei einlesen.
            </Meldung>
          )}
          {!!konten.daten?.length && (
            <ul className="ml-konten">
              {konten.daten.map((k) => (
                <KontoEintrag key={k.id} k={k} bearbeiten={() => setDialog({ konto: k })} fertig={allesNeu} />
              ))}
            </ul>
          )}
          <p className="leise klein" style={{ margin: 0 }}>
            Beholder liest die Postfächer alle {s?.intervallMinuten ?? 3} Minuten – <strong>nur lesen</strong>: nichts wird verschickt, gelöscht,
            verschoben oder als gelesen markiert. Der Spam-Ordner wird mitgeprüft; Mails daraus sind deutlich markiert und werden nie automatisch
            einsortiert. Die KI ordnet erst ab 90 % Sicherheit selbst zu.
          </p>
        </div>
      </Karte>

      <Karte titel=".eml-Dateien einlesen">
        <div className="stapel">
          <Meldung>{einlesen.fehler}</Meldung>
          <div className="reihe">
            <input ref={datei} type="file" accept=".eml,message/rfc822" multiple hidden onChange={(e) => void emlEinlesen(e.target.files)} />
            <Knopf disabled={einlesen.laeuft} onClick={() => datei.current?.click()}>
              {einlesen.laeuft ? 'Wird eingelesen …' : '.eml einlesen'}
            </Knopf>
            <Auswahl value={ziel} onChange={(e) => setZiel(e.target.value)} style={{ width: 'auto', maxWidth: '100%' }} aria-label="Ziel beim Einlesen">
              <option value="">automatisch einsortieren</option>
              {offeneProjekte.map((p) => (
                <option key={p.id} value={p.id}>
                  direkt in „{p.name}“
                </option>
              ))}
            </Auswahl>
          </div>
          <p className="leise klein" style={{ margin: 0 }}>
            „Automatisch“ sortiert wie beim Abruf: Plus-Adresse, sonst Vorschlag der KI, sonst „Unsortiert“.
          </p>
        </div>
      </Karte>

      <Karte
        titel={`${korb ? 'Papierkorb des Posteingangs' : 'Posteingang „Unsortiert“'}${liste.daten ? ` (${liste.daten.length})` : ''}`}
        aktion={
          <div className="reihe">
            {!korb && (
              <label className="schalter klein">
                <input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> Ausgeblendete zeigen
              </label>
            )}
            <label className="schalter klein">
              <input type="checkbox" checked={korb} onChange={(e) => setKorb(e.target.checked)} /> Papierkorb
            </label>
          </div>
        }
      >
        <Meldung>{liste.fehler}</Meldung>
        {korb && (
          <p className="leise klein" style={{ marginTop: 0 }}>
            Gelöschte Mails ohne Projekt bleiben 180 Tage hier und werden dann endgültig entfernt. Mails aus Projekten liegen im Papierkorb des
            jeweiligen Projekts. Im Postfach selbst wird nie etwas gelöscht.
          </p>
        )}
        {!liste.daten && !liste.fehler && <Laden />}
        {liste.daten?.length === 0 &&
          (korb ? <Leer titel="Papierkorb ist leer" /> : <Leer titel="Alles einsortiert">Mails ohne passendes Projekt landen hier.</Leer>)}
        <ul className="ml-liste ml-unsortiert">
          {liste.daten?.map((m) => (
            <UnsortiertEintrag key={m.id} m={m} projekte={offeneProjekte} fertig={allesNeu} />
          ))}
        </ul>
      </Karte>

      {dialog && (
        <KontoDialog
          konto={dialog.konto}
          schliessen={() => setDialog(null)}
          fertig={() => {
            setDialog(null);
            allesNeu();
            // Der erste Abruf läuft im Hintergrund – nach kurzer Zeit nachsehen
            setTimeout(allesNeu, 4000);
          }}
        />
      )}
    </div>
  );
}

function KontoEintrag({ k, bearbeiten, fertig }: { k: MailKonto; bearbeiten(): void; fertig(): void }) {
  const a = useAktion();
  async function schalten() {
    const e: MailKontoEingabe = { name: k.name, adresse: k.adresse, host: k.host, port: k.port, benutzer: k.benutzer, aktiv: !k.aktiv, spamPruefen: k.spamPruefen };
    if (await a.ausfuehren(() => api.put(`/api/modul/mail/konten/${k.id}`, e))) fertig();
  }
  async function entfernen() {
    if (
      !window.confirm(
        `Postfach „${k.name}“ (${k.adresse}) aus Beholder entfernen? Die Zugangsdaten werden sofort gelöscht, die ${k.mails} schon geholten Mails bleiben. Im Postfach selbst ändert sich nichts.`,
      )
    )
      return;
    if (await a.ausfuehren(() => api.delete(`/api/modul/mail/konten/${k.id}`))) fertig();
  }
  return (
    <li className={`ml-konto${k.aktiv ? '' : ' ml-konto-aus'}`}>
      <div className="ml-konto-text">
        <strong>
          {k.name} <span className="leise">‹{k.adresse}›</span>
        </strong>
        <span className="leise klein">
          {k.aktiv ? (k.laeuft ? 'wird gerade abgerufen' : `zuletzt abgerufen: ${k.letzterAbruf ? datumZeit(k.letzterAbruf) : 'noch nie'}`) : 'abgeschaltet'}
          {` · ${k.mails} Mail${k.mails === 1 ? '' : 's'} in Beholder`}
          {k.spamPruefen ? ' · Spam-Ordner wird mitgeprüft' : ' · ohne Spam-Ordner'}
          {k.host !== MAIL_GMAIL.host && ` · ${k.host}:${k.port}`}
          {k.ausEnv && ' · aus der .env übernommen'}
        </span>
        {k.aktiv && k.letzterFehler && <Meldung>{k.letzterFehler}</Meldung>}
        <Meldung>{a.fehler}</Meldung>
      </div>
      <div className="reihe">
        <Knopf klein disabled={a.laeuft} onClick={bearbeiten}>
          Bearbeiten
        </Knopf>
        <Knopf klein disabled={a.laeuft} onClick={() => void schalten()}>
          {k.aktiv ? 'Abschalten' : 'Einschalten'}
        </Knopf>
        <Knopf klein art="leise" disabled={a.laeuft} onClick={() => void entfernen()}>
          Entfernen
        </Knopf>
      </div>
    </li>
  );
}

/** „Konto hinzufügen“ / „Postfach bearbeiten“ – mit kurzer Anleitung für Gmail. Das Passwort kommt nie zurück. */
function KontoDialog({ konto, schliessen, fertig }: { konto: MailKonto | null; schliessen(): void; fertig(): void }) {
  const neu = !konto;
  const [name, setName] = useState(konto?.name ?? 'Gmail');
  const [adresse, setAdresse] = useState(konto?.adresse ?? '');
  const [benutzer, setBenutzer] = useState(konto?.benutzer ?? '');
  const [benutzerEigen, setBenutzerEigen] = useState(!!konto && konto.benutzer !== konto.adresse);
  const [passwort, setPasswort] = useState('');
  const [host, setHost] = useState(konto?.host ?? MAIL_GMAIL.host);
  const [port, setPort] = useState(String(konto?.port ?? MAIL_GMAIL.port));
  const [spam, setSpam] = useState(konto?.spamPruefen ?? true);
  const [test, setTest] = useState<MailVerbindungsTest | null>(null);
  const testen = useAktion();
  const speichern = useAktion();
  const gmail = host.trim().toLowerCase() === MAIL_GMAIL.host;
  const anmeldung = benutzerEigen ? benutzer : adresse;

  const eingabe = (): MailKontoEingabe => ({
    name: name.trim(),
    adresse: adresse.trim(),
    host: host.trim(),
    port: Number(port) || 0,
    benutzer: anmeldung.trim(),
    ...(passwort ? { passwort } : {}),
    aktiv: konto?.aktiv ?? true,
    spamPruefen: spam,
  });

  async function verbindungTesten() {
    setTest(null);
    const e = eingabe();
    const r = await testen.ausfuehren(() =>
      api.post<MailVerbindungsTest>('/api/modul/mail/konten/test', {
        host: e.host,
        port: e.port,
        benutzer: e.benutzer,
        ...(passwort ? { passwort } : {}),
        ...(konto ? { kontoId: konto.id } : {}),
      }),
    );
    if (r) setTest(r);
  }

  async function sichern() {
    const r = await speichern.ausfuehren(() =>
      neu ? api.post<MailKonto>('/api/modul/mail/konten', eingabe()) : api.put<MailKonto>(`/api/modul/mail/konten/${konto.id}`, eingabe()),
    );
    if (r) fertig();
  }

  const laeuft = testen.laeuft || speichern.laeuft;
  const vollstaendig = !!name.trim() && !!adresse.trim() && !!anmeldung.trim() && (!neu || !!passwort) && !!host.trim() && Number(port) > 0;
  return (
    <Dialog
      titel={neu ? 'Konto hinzufügen' : `Postfach „${konto.name}“ bearbeiten`}
      offen
      schliessen={schliessen}
      fuss={
        <>
          <Knopf disabled={!vollstaendig || laeuft} onClick={() => void verbindungTesten()}>
            {testen.laeuft ? 'Wird getestet …' : 'Verbindung testen'}
          </Knopf>
          <Knopf art="haupt" disabled={!vollstaendig || laeuft} onClick={() => void sichern()}>
            {speichern.laeuft ? 'Wird gespeichert …' : neu ? 'Hinzufügen' : 'Speichern'}
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
        </>
      }
    >
      <div className="stapel">
        {gmail && (
          <details open={neu}>
            <summary>So geht es bei Gmail (App-Passwort)</summary>
            <ol className="ml-anleitung klein">
              <li>
                Im Google-Konto unter „Sicherheit“ die <strong>Bestätigung in zwei Schritten</strong> einschalten (falls noch nicht an).
              </li>
              <li>
                Danach unter <code>myaccount.google.com/apppasswords</code> ein <strong>App-Passwort</strong> anlegen, Name z. B. „Beholder“.
                Google zeigt 16 Zeichen an – die hier eintragen (Leerzeichen sind egal).
              </li>
              <li>Nicht das normale Google-Passwort benutzen. Das App-Passwort lässt sich bei Google jederzeit wieder löschen.</li>
              <li>„Verbindung testen“ drücken, dann „{neu ? 'Hinzufügen' : 'Speichern'}“. Der erste Abruf holt nach und nach alle vorhandenen Mails.</li>
            </ol>
          </details>
        )}
        <div className="ml-zwei">
          <Feld titel="Adresse">
            <Eingabe
              type="email"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
              placeholder="name@gmail.com"
              autoComplete="off"
              maxLength={200}
            />
          </Feld>
          <Feld titel="Name in Beholder">
            <Eingabe value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </Feld>
        </div>
        <Feld
          titel={neu ? 'App-Passwort' : 'Neues App-Passwort'}
          hinweis={
            neu
              ? 'Wird verschlüsselt gespeichert und nie wieder angezeigt.'
              : 'Leer lassen, um das gespeicherte Passwort zu behalten. Es wird nie angezeigt.'
          }
        >
          <Eingabe
            type="password"
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            autoComplete="new-password"
            placeholder={neu ? 'xxxx xxxx xxxx xxxx' : 'unverändert'}
            maxLength={200}
          />
        </Feld>
        <label className="schalter">
          <input type="checkbox" checked={spam} onChange={(e) => setSpam(e.target.checked)} />
          <span>
            Spam-Ordner mitprüfen
            <br />
            <span className="leise klein">Mails daraus werden deutlich markiert; Belege und Aufgaben entstehen daraus nie automatisch.</span>
          </span>
        </label>
        <details open={!gmail || benutzerEigen}>
          <summary>Server und Anmeldung (für andere Anbieter)</summary>
          <div className="stapel" style={{ marginTop: 8 }}>
            <div className="ml-zwei">
              <Feld titel="IMAP-Server">
                <Eingabe value={host} onChange={(e) => setHost(e.target.value)} autoComplete="off" maxLength={200} />
              </Feld>
              <Feld titel="Port" hinweis="993 = verschlüsselt">
                <Eingabe value={port} inputMode="numeric" onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} maxLength={5} />
              </Feld>
            </div>
            <label className="schalter">
              <input type="checkbox" checked={benutzerEigen} onChange={(e) => setBenutzerEigen(e.target.checked)} /> Benutzername weicht von der
              Adresse ab
            </label>
            {benutzerEigen && (
              <Feld titel="Benutzername">
                <Eingabe value={benutzer} onChange={(e) => setBenutzer(e.target.value)} autoComplete="off" maxLength={200} />
              </Feld>
            )}
            {!gmail && (
              <Knopf
                klein
                art="leise"
                onClick={() => {
                  setHost(MAIL_GMAIL.host);
                  setPort(String(MAIL_GMAIL.port));
                }}
              >
                Gmail-Werte eintragen
              </Knopf>
            )}
          </div>
        </details>
        <Meldung>{testen.fehler || speichern.fehler}</Meldung>
        {test && <Meldung art={test.ok ? 'ok' : 'fehler'}>{test.text}</Meldung>}
        {konto?.ausEnv && (
          <p className="leise klein" style={{ margin: 0 }}>
            Dieses Postfach wurde einmalig aus der .env (IMAP_*) übernommen und wird jetzt nur noch hier verwaltet.
          </p>
        )}
      </div>
    </Dialog>
  );
}

function UnsortiertEintrag({ m, projekte, fertig }: { m: MailUnsortiert; projekte: MeinProjekt[]; fertig(): void }) {
  const [ziel, setZiel] = useState(m.vorschlagProjektId ?? '');
  const a = useAktion();
  const imKorb = !!m.geloeschtAm;
  const los = async (fn: () => Promise<unknown>) => {
    if (await a.ausfuehren(fn)) fertig();
  };
  return (
    <li className={`ml-eintrag${m.ausgeblendet ? ' ml-ausgeblendet' : ''}`}>
      <div className="ml-eintrag-kopf">
        <strong>{m.betreff}</strong>
        <ArtMarke art={m.art} />
        <Verdacht m={m} />
      </div>
      <div className="klein">
        <Absender m={m} /> · <span className="leise">{wann(m)}</span>
        {m.an && <span className="leise"> · an {m.an}</span>}
        {m.kontoName && <span className="leise"> · {m.kontoName}{m.ausSpam ? ', Spam-Ordner' : ''}</span>}
      </div>
      {m.zusammenfassung && <div className="leise zeilen-2">{m.zusammenfassung}</div>}
      <div className="klein leise">
        {imKorb ? `Im Papierkorb seit ${datumZeit(m.geloeschtAm!)}.` : m.zuordnungInfo}
        {!imKorb && m.vorschlagProjektName && ` Vorschlag: „${m.vorschlagProjektName}“.`}
      </div>
      <details>
        <summary>Text, Links und Anhänge ansehen</summary>
        <div className="stapel" style={{ marginTop: 8 }}>
          <pre className="ml-text">{m.text || '(leer)'}</pre>
          <LinkTabelle m={m} />
          {m.anhangListe.length > 0 && (
            <p className="klein leise" style={{ margin: 0 }}>
              Anhänge (nach dem Zuordnen herunterladbar): {m.anhangListe.map((x) => x.name).join(', ')}
            </p>
          )}
        </div>
      </details>
      <Meldung>{a.fehler}</Meldung>
      {imKorb ? (
        <div className="reihe">
          <Knopf disabled={a.laeuft} onClick={() => void los(() => api.post(`/api/modul/mail/unsortiert/${m.id}/wiederherstellen`))}>
            Zurückholen
          </Knopf>
          <Knopf
            art="gefahr"
            disabled={a.laeuft}
            onClick={() => {
              if (window.confirm(`„${m.betreff}“ endgültig löschen? Das lässt sich nicht rückgängig machen. Im Postfach ändert sich nichts.`)) {
                void los(() => api.delete(`/api/modul/mail/unsortiert/${m.id}`));
              }
            }}
          >
            Endgültig löschen
          </Knopf>
        </div>
      ) : (
        <div className="reihe">
          <Auswahl value={ziel} onChange={(e) => setZiel(e.target.value)} style={{ width: 'auto', maxWidth: '100%' }} aria-label="Projekt">
            <option value="">– Projekt wählen –</option>
            {projekte.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.id === m.vorschlagProjektId ? ' (Vorschlag)' : ''}
              </option>
            ))}
          </Auswahl>
          <Knopf
            art="haupt"
            disabled={!ziel || a.laeuft}
            onClick={() => void los(() => api.post(`/api/modul/mail/unsortiert/${m.id}/zuordnen`, { projektId: ziel }))}
          >
            {a.laeuft ? 'Wird einsortiert …' : 'Zuordnen'}
          </Knopf>
          <Knopf
            art="leise"
            disabled={a.laeuft}
            onClick={() => void los(() => api.post(`/api/modul/mail/unsortiert/${m.id}/ausblenden`, { ausgeblendet: !m.ausgeblendet }))}
          >
            {m.ausgeblendet ? 'Wieder einblenden' : 'Ausblenden'}
          </Knopf>
          <Knopf
            art="leise"
            disabled={a.laeuft}
            title="Nur in Beholder – im Postfach bleibt die Mail unverändert"
            onClick={() => void los(() => api.post(`/api/modul/mail/unsortiert/${m.id}/loeschen`))}
          >
            In den Papierkorb
          </Knopf>
        </div>
      )}
    </li>
  );
}
